/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  SidePanel,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';

import {
  ALERT_BANNER_DEFAULT,
  CF_NEU_STL,
  GLOBAL_ALERT_BANNER_TYPES,
  l,
  SIDEPANEL_ANIMATION_DURATION,
} from '../../../../shared/constants';
import { useTypedSelector } from '../../../../shared/hooks';
import { globalActions } from '../../../../shared/store/globalSlice';
import {
  type PolicyApiItem,
  type ServiceIdentityItem,
  type ServiceIdentitySPItem,
  type ServiceIdSPItemSA,
} from '../../../../shared/types';
import { serviceIdentityActions } from '../../data-store/serviceIdentitySlice';
import { useGetServiceIdDetails, useUpdateServiceIdentity } from '../../hooks';
import {
  getDescCriticalText,
  getDescIsCritical,
  getStatusKind,
  haveSameStringSet,
} from '../../utils';
import { ServiceIdentityListAction } from '../ServiceIdentityListAction';

import HistoryContent from './HistoryContent';
import SummaryContent from './SummaryContent';

import './ServiceIdentitySP.css';

interface ServiceIdentitySPProps {
  isOpen: boolean;
  onClose: () => void;
  dataItem: ServiceIdentityItem | null;
}

/**
 * Side panel for viewing and managing a service identity's details,
 * with tabbed navigation (Summary / History) and animated open/close transitions.
 * Summary data fetching and saving are managed here; history fetching is
 * delegated to {@link HistoryContent}.
 * @param props - {@link ServiceIdentitySPProps}
 * @param props.isOpen - Whether the side panel is open.
 * @param props.onClose - Callback invoked when the panel is closed.
 * @param props.dataItem - The service identity item to display, or null.
 * @returns The ServiceIdentitySP side panel component, or null when not rendering.
 */
function ServiceIdentitySP({
  isOpen,
  onClose,
  dataItem,
}: ServiceIdentitySPProps) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { rowsToUpdate } = useTypedSelector(
    (state) => state.savicopilot.serviceIdentity.list
  );

  // States and ref
  const sidepanelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number>();

  // States
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  // Summary
  const [summaryData, setSummaryData] = useState<ServiceIdentitySPItem | null>(
    null
  );
  const [currentSummaryData, setCurrentSummaryData] =
    useState<ServiceIdentitySPItem | null>(null);

  // Constants
  const itemId = useMemo(() => dataItem?.id, [dataItem?.id]);
  const hasSummaryChanges = useMemo(() => {
    const isTagSimilar = haveSameStringSet(
      summaryData?.tags ?? [],
      currentSummaryData?.tags ?? []
    );
    const isPolicySimilar = Boolean(
      (summaryData?.passwordPolicy === null &&
        currentSummaryData?.passwordPolicy === null) ||
        summaryData?.passwordPolicy?.id ===
          currentSummaryData?.passwordPolicy?.id
    );
    const isDescSimilar =
      summaryData?.description === currentSummaryData?.description;

    return !isTagSimilar || !isPolicySimilar || !isDescSimilar;
  }, [summaryData, currentSummaryData]);

  // API hooks
  const { handleFetchSummary, isSummaryError, isSummaryLoading } =
    useGetServiceIdDetails(itemId);
  const { handleUpdateIdentity, isSaving } = useUpdateServiceIdentity(itemId);

  const triggerFetchSummary = useCallback(() => {
    setSummaryData(null);
    setCurrentSummaryData(null);

    handleFetchSummary()
      .then((response) => {
        const typedResponse = response as ServiceIdentitySPItem & {
          success: boolean;
          msg: string;
        };

        if (!typedResponse.success) {
          throw Error(typedResponse.msg);
        }

        setSummaryData(response as ServiceIdentitySPItem);
        setCurrentSummaryData(response as ServiceIdentitySPItem);
      })
      .catch((error) => {
        setSummaryData(null);
        setCurrentSummaryData(null);

        Logger.log('Failed fetching summary ', error);
      });
  }, [handleFetchSummary]);

  const triggerSaveIdentity = useCallback(() => {
    const data: {
      description?: string | null;
      tags?: string[] | null;
      passwordPolicy?: number | null;
    } = {};

    const isDescCriptionCritical = getDescIsCritical(
      currentSummaryData?.description ?? ''
    );

    if (isDescCriptionCritical) {
      dispatch(
        globalActions.setGlobalAlertBanner({
          ...ALERT_BANNER_DEFAULT,
          kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
          title: '',
          description: getDescCriticalText(
            currentSummaryData?.description ?? ''
          ),
          isOpen: true,
        })
      );

      return;
    }

    const isDescSimilar =
      summaryData?.description === currentSummaryData?.description;
    const isPolicySimilar = Boolean(
      (summaryData?.passwordPolicy === null &&
        currentSummaryData?.passwordPolicy === null) ||
        summaryData?.passwordPolicy?.id ===
          currentSummaryData?.passwordPolicy?.id
    );
    const isTagSimilar = haveSameStringSet(
      summaryData?.tags ?? [],
      currentSummaryData?.tags ?? []
    );

    if (!isDescSimilar) {
      data.description = currentSummaryData?.description ?? null;
    }

    if (!isPolicySimilar) {
      data.passwordPolicy = currentSummaryData?.passwordPolicy?.id ?? null;
    }

    if (!isTagSimilar) {
      data.tags = currentSummaryData?.tags ?? null;
    }

    handleUpdateIdentity(data)
      .then((response) => {
        const typedResponse = response as { success: boolean; msg: string };

        if (typedResponse.success) {
          const rowUpdatePayload: {
            description?: string;
            tags?: string[];
            passwordPolicy?: PolicyApiItem | null;
          } = {};

          if (!isDescSimilar) {
            rowUpdatePayload.description =
              currentSummaryData?.description ?? '';
          }

          if (!isTagSimilar) {
            rowUpdatePayload.tags = currentSummaryData?.tags ?? [];
          }

          if (!isPolicySimilar) {
            rowUpdatePayload.passwordPolicy =
              currentSummaryData?.passwordPolicy ?? null;
          }

          batch(() => {
            dispatch(
              globalActions.setGlobalAlertBanner({
                ...ALERT_BANNER_DEFAULT,
                kind: GLOBAL_ALERT_BANNER_TYPES.SUCCESS,
                title: '',
                description: typedResponse.msg,
                isOpen: true,
              })
            );

            if (itemId) {
              dispatch(
                serviceIdentityActions.setRowsToUpdate({
                  [itemId]: rowUpdatePayload,
                })
              );
            }
          });

          setSummaryData(currentSummaryData);

          return;
        }

        dispatch(
          globalActions.setGlobalAlertBanner({
            ...ALERT_BANNER_DEFAULT,
            kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
            title: 'Failed',
            description: typedResponse.msg,
            isOpen: true,
          })
        );
      })
      .catch((error) => {
        const typedError = error as {
          data?: { response?: { data?: { msg?: string } } };
        };

        dispatch(
          globalActions.setGlobalAlertBanner({
            ...ALERT_BANNER_DEFAULT,
            kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
            title: '',
            description:
              typedError.data?.response?.data?.msg ??
              'Failed to save service identity',
            isOpen: true,
          })
        );
      });
  }, [dispatch, itemId, summaryData, currentSummaryData, handleUpdateIdentity]);

  // Effects
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);

      requestAnimationFrame(() => {
        setIsAnimatingOpen(true);
      });

      return;
    }

    setIsAnimatingOpen(false);

    closeTimerRef.current = window.setTimeout(() => {
      setShouldRender(false);
    }, SIDEPANEL_ANIMATION_DURATION);

    // eslint-disable-next-line consistent-return
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    setActiveTab('1');
  }, [itemId, shouldRender]);

  useEffect(() => {
    if (!itemId || !shouldRender || activeTab !== '1') {
      return;
    }

    triggerFetchSummary();
  }, [itemId, activeTab, shouldRender, triggerFetchSummary]);

  useEffect(() => {
    if (!isOpen || !rowsToUpdate || !itemId) {
      return;
    }

    setCurrentSummaryData((prev) => {
      if (!prev) {
        return prev;
      }

      const newProps = rowsToUpdate[itemId];
      const patch: Partial<ServiceIdentitySPItem> = {};

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!newProps) {
        return prev;
      }

      if ('serviceAccounts' in newProps) {
        return {
          ...prev,
          serviceAccounts: newProps.serviceAccounts?.map((sa) => ({
            id: sa.accountKey,
            name: sa.accountName,
            status: sa.status,
            endpoint: sa.endpointName,
            icon: sa.endpointLogo,
          })) as ServiceIdSPItemSA[],
        };
      }

      if ('status' in newProps) {
        patch.status = newProps.status;
      }

      if ('owners' in newProps) {
        patch.owners = newProps.owners;
      }

      return { ...prev, ...patch };
    });
  }, [isOpen, itemId, rowsToUpdate]);

  // Utils
  const handleCancelChanges = useCallback(() => {
    setCurrentSummaryData(summaryData);
  }, [summaryData]);

  const handleSaveChanges = useCallback(() => {
    if (!hasSummaryChanges) {
      return;
    }

    triggerSaveIdentity();
  }, [hasSummaryChanges, triggerSaveIdentity]);

  const shouldShowEnableAction = useCallback(() => {
    if (currentSummaryData) {
      return (
        currentSummaryData.status.toLowerCase() === 'inactive' ||
        currentSummaryData.status.toLowerCase() === 'disabled'
      );
    }

    return (
      dataItem?.status.toLowerCase() === 'inactive' ||
      dataItem?.status.toLowerCase() === 'disabled'
    );
  }, [currentSummaryData, dataItem]);

  if (!shouldRender || !dataItem || !itemId) {
    return null;
  }

  const { statusColor, statusTextKey } = getStatusKind(
    currentSummaryData?.status ?? dataItem.status
  );

  const isDeleted = Boolean(
    currentSummaryData?.status.toLowerCase().includes('delete')
  );

  const renderFooter = () => {
    if (activeTab === '1' && hasSummaryChanges) {
      return (
        <FooterActionBar
          size='large'
          className='ServiceIdentitySP-footerActionBar'>
          <div />
          <Box
            tag='div'
            className='ServiceIdentitySP-footerContainer ServiceIdentitySP-footerContainer--isChange'>
            <Button
              kind='ghost'
              isDisabled={isSaving}
              onClick={() => handleCancelChanges()}
              className='ServiceIdentitySP-footerCancelBtn'>
              <FormattedMessage
                id='6PdOcy'
                defaultMessage='Cancel'
                description='Cancel'
              />
            </Button>
            <Button
              kind='filled'
              onClick={() => handleSaveChanges()}
              isDisabled={isSaving}
              isLoading={isSaving}
              loaderText={intl.formatMessage({
                id: 'WmDyLh',
                defaultMessage: 'Saving...',
                description: 'Saving',
              })}>
              <FormattedMessage
                id='0CvRZW'
                defaultMessage='Save'
                description='Save'
              />
            </Button>
          </Box>
        </FooterActionBar>
      );
    }

    if (activeTab === '1' && !isDeleted) {
      return (
        <ServiceIdentityListAction
          isSidePanel
          serviceIdentityId={dataItem.id}
          policyId={dataItem.passwordPolicy?.id}
          isError={isSummaryError}
          isLoading={isSummaryLoading}
          showEnableAction={shouldShowEnableAction()}
          isDisabled={
            dataItem.status.toLowerCase() === 'disabled' || isSummaryError
          }
        />
      );
    }

    return undefined;
  };

  return (
    <SidePanel
      ref={sidepanelRef}
      isOpen={shouldRender}
      onClose={onClose}
      header={{
        title: dataItem.name,
        // Description supports the element but type has not been added in DS
        description: (
          <Box tag='div' className='ServiceIdentitySP-headerDescription'>
            <Typography kind='body3' color={CF_NEU_STL} lineLimit={2}>
              {currentSummaryData?.description ?? ' '}
            </Typography>
            <Typography kind='body3' color={statusColor}>
              {`● ${intl.formatMessage(l[statusTextKey])}`}
            </Typography>
          </Box>
        ) as unknown as string,
        icon: 'ServiceAccount',
      }}
      layout={
        <Box tag='div' className='ServiceIdentitySP-layoutContainer'>
          <TabGroup
            moreContainerRef={sidepanelRef}
            value={activeTab}
            onChange={(tab: string) => {
              setActiveTab(tab);
            }}
            size='medium'>
            <TabItem
              label={intl.formatMessage({
                id: 'IBxPyG',
                defaultMessage: 'Summary',
                description: 'Summary',
              })}
              value='1'
              icon={<Icon kind='Document' color={CF_NEU_STL} />}
            />
            <TabItem
              label={intl.formatMessage({
                id: 'Zxtbyk',
                defaultMessage: 'History',
                description: 'History',
              })}
              value='2'
              icon={<Icon kind='Time' color={CF_NEU_STL} />}
            />
          </TabGroup>
          <SummaryContent
            activeTab={activeTab}
            data={currentSummaryData}
            policyId={summaryData?.passwordPolicy?.id ?? null}
            serviceId={itemId}
            isLoading={isSummaryLoading}
            isDisabled={isSaving || isDeleted}
            isError={isSummaryError}
            onRefresh={() => triggerFetchSummary()}
            setCurrentData={setCurrentSummaryData}
            hasSummaryChanges={hasSummaryChanges}
          />
          <HistoryContent
            activeTab={activeTab}
            itemId={itemId}
            shouldRender={shouldRender}
          />
        </Box>
      }
      footer={renderFooter()}
      isSidePanelNavigatingList
      className='ServiceIdentitySP'
      classNames={{
        headerClassName: 'ServiceIdentitySP-header',
        layoutClassName: 'ServiceIdentitySP-layout',
        innerContentClassName: `ServiceIdentitySP-innerContent ${
          isAnimatingOpen ? 'ServiceIdentitySP-innerContent--isOpen' : ''
        }`,

        footerClassName: 'ServiceIdentitySP-footer',
      }}
    />
  );
}

export default ServiceIdentitySP;
